import React, { FC, memo, useCallback, useContext } from "react";
import {
    getPointType,
    isDashedSection as isDashedSectionHelper,
    iLineChartConfig,
    iViewBoxProps,
} from "./LineChart.helpers";
import LineChartDot from "./LineChartDot";
import { LineChartThemeContext } from "./LineChartTheme.context";

const LineChartColumns: FC<{
    viewport: iViewBoxProps;
    getY: (value: number) => number;
    config: iLineChartConfig;
    setActiveTimeIndex: (index: number) => void;
}> = (
    {
        viewport,
        config,
        getY,
        setActiveTimeIndex,
    }
) => {
    const { THEME } = useContext(LineChartThemeContext);
    const [hoveredTimeIndex, setHoveredTimeIndex] = React.useState(-1);
    
    const handleTimePointMouseEnter = useCallback((index: number) => {
        setActiveTimeIndex(index);
    }, [setActiveTimeIndex]);

    const handleTimePointMouseLeave = useCallback(() => {
        setActiveTimeIndex(-1);
    }, [setActiveTimeIndex]);

    const handleMouseEnter = useCallback((index: number) => {
        setHoveredTimeIndex(index);
        handleTimePointMouseEnter(index);
    }, [handleTimePointMouseEnter]);

    const handleMouseLeave = useCallback(() => {
        setHoveredTimeIndex(-1);
        handleTimePointMouseLeave();
    }, [handleTimePointMouseLeave]);
    
    if (!config.series[0]) return null;
    
    const timePointWidth = viewport.width / config.series[0].data.length;

    return (
        <g name="time-points" className="LineChart__time-points">
            {config.series[0].data.map((timePoint, timeIndex) => {
                const isDashed = isDashedSectionHelper(timeIndex, config);
                const isHovered = hoveredTimeIndex === timeIndex;
                
                return (
                    <g
                        key={`${timePoint.label}-${timeIndex}`}
                        name="time-point"
                        className="LineChart__time-point"
                        onMouseEnter={() => handleMouseEnter(timeIndex)}
                        onMouseLeave={handleMouseLeave}
                    >
                        <rect
                            width={timePointWidth}
                            height={viewport.height + viewport.padding.top + viewport.padding.bottom}
                            x={timePointWidth * timeIndex}
                            y={-viewport.padding.top}
                            fill="transparent"
                            stroke="transparent"
                        />
                        
                        {/* Active column highlighting - only on hover with smooth transitions */}
                        <rect
                            x={timePointWidth * timeIndex}
                            y={viewport.height}
                            height={viewport.height}
                            width={timePointWidth}
                            fill={`${THEME.CELL.ACTIVE}`}
                            fillOpacity={isHovered ? 1 : 0}
                            fontFamily={"font_text"}
                        />
                        <line
                            x1={timePointWidth * timeIndex}
                            y1={viewport.height + viewport.padding.bottom}
                            x2={timePointWidth * (timeIndex + 1)}
                            y2={viewport.height + viewport.padding.bottom}
                            stroke="#F55353"
                            strokeWidth={6}
                            strokeOpacity={isHovered ? 1 : 0}
                        />
                        
                        {/* Background rendering is now handled in main LineChart component */}
                        
                        {/* Render dots for all series - only visible when hovering this column */}
                        {isHovered && config.series.map((series, seriesIndex) => {
                            const seriesTimePoint = series.data[timeIndex];
                            if (!seriesTimePoint) return null;
                            
                            const pointCount = seriesTimePoint.points.length;
                            const pointWidth = timePointWidth / pointCount;
                            
                            return (
                                <React.Fragment key={`${series.id}-${timeIndex}`}>
                                    {seriesTimePoint.points.map((point, pointIndex) => {
                                        const cx = timePointWidth * timeIndex + pointWidth * (pointIndex + 0.5);
                                        const cy = getY(point.value);
                                        const pointType = getPointType(seriesTimePoint, pointIndex, isDashed);
                                        
                                        return (
                                            <LineChartDot
                                                key={`${series.id}-${timeIndex}-${pointIndex}`}
                                                cx={cx}
                                                cy={cy}
                                                color={series.color}
                                                type={pointType}
                                                isHovered={true}
                                            />
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                        
                        {/* Vertical grid lines - only visible on hover */}
                        <line
                            x1={timePointWidth * timeIndex}
                            y1={-viewport.padding.top}
                            x2={timePointWidth * timeIndex}
                            y2={viewport.height + viewport.padding.bottom}
                            stroke={THEME.BORDER.DEFAULT}
                            strokeOpacity={isHovered ? 1 : 0}
                            style={{ transition: 'stroke-opacity 200ms ease' }}
                        />
                        <line
                            x1={timePointWidth * (timeIndex + 1)}
                            y1={-viewport.padding.top}
                            x2={timePointWidth * (timeIndex + 1)}
                            y2={viewport.height + viewport.padding.bottom}
                            stroke={THEME.BORDER.DEFAULT}
                            strokeOpacity={isHovered ? 1 : 0}
                            style={{ transition: 'stroke-opacity 200ms ease' }}
                        />
                    </g>
                );
            })}
        </g>
    );
};

export default memo(LineChartColumns);